import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'

import { useTranslation } from 'react-i18next'

// MUI Imports
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import { Email, Search, Refresh } from '@mui/icons-material'
import {
  Box,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Autocomplete,
  Checkbox as MuiCheckbox
} from '@mui/material'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'

// Third-party Imports
import { rankItem } from '@tanstack/match-sorter-utils'

// Component Imports
import CampaignReviewTable from '../organisms/CampaignReviewTable'
import EmailEditDialog from '../molecules/EmailEditDialog'
import StartMailingConfirmDialog from '../molecules/StartMailingConfirmDialog'
import CampaignProgressBar
  from '../../../../../campaigns/components/CampaignProgressBar'

import {
  getCampaignFirmsAndState,
  getCampaign,
  updateCampaignFirmEmailContent,
  startMailing, downloadCampaignFirmsExcel
} from '../../../services/B2BServices'
import { getUrlParams } from '../../../services/urlServices'
import { useToast } from '../../../../../campaigns/hooks/useToast'
import { useGlobalStoreActions } from '../../../../../store/globalStore'
import SendIcon from '@mui/icons-material/Send'
import TableViewIcon from '@mui/icons-material/Download'

const fuzzyFilter = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(columnId), value)

  addMeta({
    itemRank
  })

  return itemRank.passed
}

const DEFAULT_PAGE = 0
const DEFAULT_PAGE_SIZE = 10
const DEFAULT_TOTAL_ELEMENTS = 0
const DEFAULT_TOTAL_PAGE_COUNT = 0
const DEFAULT_FILTERS = { selected: true }

const SORT_FIELD_MAP = {
  name: 'firmName'
}

const buildSortingParams = (sorting) => {
  const sortBy = SORT_FIELD_MAP[sorting?.[0]?.id]

  if (!sortBy) return {}

  return { sortBy, direction: sorting[0].desc ? 'DESC' : 'ASC' }
}

const CampaignReview = ({ onCampaignUpdate, campaign, showProgressBar = false, disableActions = false }) => {
  const { t } = useTranslation()

  const queryParams = getUrlParams()

  const { showSuccess, showError } = useToast()
  const { setCampaignStatus: setStoreCampaignStatus } = useGlobalStoreActions()

  const [firms, setFirms] = useState([])
  const [campaignData, setCampaignData] = useState(null)
  const [campaignStatus, setCampaignStatus] = useState(null)
  const [progressStatistics, setProgressStatistics] = useState(null)

  const [fetching, setFetching] = useState(true)
  const [startMailingLoading, setStartMailingLoading] = useState(false)

  const intervalRef = useRef(null)

  const [emailEditState, setEmailEditState] = useState({
    open: false,
    selectedContact: null,
    selectedFirm: null,
    saving: false
  })

  const [confirmMailingDialogOpen, setConfirmMailingDialogOpen] = useState(false)

  const [sorting, setSorting] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [suitabilityFilters, setSuitabilityFilters] = useState([])

  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE)
  const [totalElements, setTotalElements] = useState(DEFAULT_TOTAL_ELEMENTS)
  const [totalPageCount, setTotalPageCount] = useState(DEFAULT_TOTAL_PAGE_COUNT)

  const suitabilityOptions = useMemo(() => [
    { value: 1, label: t('mailTemplate.suitabilityLevel.highlySuitable'), key: 'HIGHLY_SUITABLE' },
    { value: 2, label: t('mailTemplate.suitabilityLevel.moderatelySuitable'), key: 'MODERATELY_SUITABLE' },
    { value: 3, label: t('mailTemplate.suitabilityLevel.minimallySuitable'), key: 'MINIMALLY_SUITABLE' },
    { value: 4, label: t('mailTemplate.suitabilityLevel.notSuitable'), key: 'NOT_SUITABLE' },
    { value: 5, label: t('mailTemplate.suitabilityLevel.cannotAccess'), key: 'CANNOT_ACCESS' }
  ], [t])

  const isDisabled = useMemo(() =>
    (campaignStatus || campaignData?.status) === 'GENERATING', [campaignStatus, campaignData?.status]
  )

  const isMailingInProgress = useMemo(() =>
    (campaignStatus || campaignData?.status) === 'MAILING', [campaignStatus, campaignData?.status]
  )

  const canStartMailing = useMemo(() =>
    (campaignStatus || campaignData?.status) === 'READY_TO_MAIL' && !disableActions, [campaignStatus, campaignData?.status, disableActions]
  )

  const handleEditEmail = useCallback(({ contact, firm }) => {
    setEmailEditState({
      open: true,
      selectedContact: contact,
      selectedFirm: firm,
      saving: false
    })
  }, [])

  const handleCloseEmailEdit = useCallback(() => {
    setEmailEditState({
      open: false,
      selectedContact: null,
      selectedFirm: null,
      saving: false
    })
  }, [])

  const buildFilters = useCallback(() => {
    const filters = {
      selected: true
    }

    if (suitabilityFilters.length > 0) {
      filters.suitabilityLevels = suitabilityFilters.map(f => f.key).join(',')
    }

    if (debouncedSearchTerm && debouncedSearchTerm.length >= 3) {
      filters.searchKeyword = debouncedSearchTerm
    }

    return filters
  }, [suitabilityFilters, debouncedSearchTerm])

  const handleSaveEmail = useCallback(async ({ firmId, contactId, subject, html }) => {
    setEmailEditState(prev => ({ ...prev, saving: true }))

    try {
      const payload = {
        campaignId: queryParams.b2bCampaignId,
        contactList: [
          {
            contactId,
            emailSubject: subject,
            emailContent: html
          }
        ]
      }

      await updateCampaignFirmEmailContent(payload)

      const filters = buildFilters()
      const sortingParams = buildSortingParams(sorting)

      const res = await getCampaignFirmsAndState({ b2bCampaignId: queryParams.b2bCampaignId }, currentPage, pageSize, filters, sortingParams)

      const firmList = Array.isArray(res.data.campaignFirms?.content) ? res.data.campaignFirms.content : []
      setFirms(firmList)
      if (res.data.campaignFirms?.totalRecordCount !== undefined) {
        setTotalElements(res.data.campaignFirms.totalRecordCount)
      }
      if (res.data.campaignFirms?.totalPageCount !== undefined) {
        setTotalPageCount(res.data.campaignFirms.totalPageCount)
      }

      showSuccess(t('mailTemplate.wizard.emailEditSuccess'))

      setTimeout(() => {
        handleCloseEmailEdit()
      }, 500)
    } catch (error) {
      console.error('Failed to update contact email:', error)

      showError(t('mailTemplate.wizard.emailEditError'))

      setEmailEditState(prev => ({ ...prev, saving: false }))
    }
  }, [queryParams.b2bCampaignId, showSuccess, showError, t, handleCloseEmailEdit, currentPage, pageSize, buildFilters, sorting])

  const handleStartMailingClick = useCallback(() => {
    setConfirmMailingDialogOpen(true)
  }, [])

  const handleConfirmStartMailing = useCallback(async () => {
    setConfirmMailingDialogOpen(false)
    setStartMailingLoading(true)

    try {
      await startMailing(queryParams.b2bCampaignId)

      showSuccess(t('mailTemplate.campaignReview.startMailingSuccess'))

      if (onCampaignUpdate) {
        await onCampaignUpdate()
      }
    } catch (error) {
      console.error('Failed to start mailing:', error)
      showError(t('mailTemplate.campaignReview.startMailingError'))
    } finally {
      setStartMailingLoading(false)
    }
  }, [queryParams.b2bCampaignId, showSuccess, showError, t, onCampaignUpdate])

  const handleCancelStartMailing = useCallback(() => {
    setConfirmMailingDialogOpen(false)
  }, [])

  const handleRefresh = useCallback((page = currentPage, size = pageSize, filters = null, sortingParams = null) => {
    if (queryParams?.b2bCampaignId) {
      setFetching(true)

      const currentFilters = filters !== null ? filters : buildFilters()
      const currentSorting = sortingParams !== null ? sortingParams : buildSortingParams(sorting)

      const minDelayPromise = new Promise(resolve => setTimeout(resolve, 800))

      const apiPromise = getCampaignFirmsAndState({ b2bCampaignId: queryParams.b2bCampaignId }, page, size, currentFilters, currentSorting)

      Promise.all([apiPromise, minDelayPromise]).then(([res]) => {
        const firmList = Array.isArray(res.data.campaignFirms?.content) ? res.data.campaignFirms.content : []
        setFirms(firmList)
        setCampaignStatus(res.data.campaignStatus)
        setCampaignData(prev => ({ ...prev, status: res.data.campaignStatus }))
        if (res.data.progressStatistics) {
          setProgressStatistics(res.data.progressStatistics)
        }
        if (res.data.campaignFirms?.totalRecordCount !== undefined) {
          setTotalElements(res.data.campaignFirms.totalRecordCount)
        }
        if (res.data.campaignFirms?.totalPageCount !== undefined) {
          setTotalPageCount(res.data.campaignFirms.totalPageCount)
        }
        if (res.data.campaignStatus) {
          setStoreCampaignStatus(queryParams.b2bCampaignId, res.data.campaignStatus)
        }
      }).finally(() => {
        setFetching(false)
      })
    }
  }, [queryParams?.b2bCampaignId, setStoreCampaignStatus, currentPage, pageSize, buildFilters, sorting])

  const queryParamsError = useCallback(
    (queryParams, b2bAccountIdError, b2bCampaignIdError) => {
      const b2bAccountId = queryParams?.b2bAccountId
      const b2bCampaignId = queryParams?.b2bCampaignId

      if (b2bAccountId && b2bCampaignId) {
        return false
      } else if (b2bCampaignId) {
        return <div>{b2bAccountIdError}</div>
      } else {
        return <div>{b2bCampaignIdError}</div>
      }
    }, [])

  useEffect(() => {
    if (queryParams?.b2bCampaignId) {
      getCampaign(queryParams.b2bCampaignId).then((campaignRes) => {
        setCampaignData(campaignRes.data)
        if (campaignRes.data?.status) {
          setStoreCampaignStatus(queryParams.b2bCampaignId,
            campaignRes.data.status)
        }
      }).catch((error) => {
        console.error('Failed to fetch campaign data:', error)
      })

      getCampaignFirmsAndState({ b2bCampaignId: queryParams.b2bCampaignId }, DEFAULT_PAGE, DEFAULT_PAGE_SIZE, DEFAULT_FILTERS).then((res) => {
        const firmList = Array.isArray(res.data.campaignFirms?.content) ? res.data.campaignFirms.content : []
        setFirms(firmList)
        setCampaignStatus(res.data.campaignStatus)
        setCampaignData(
          prev => ({ ...prev, status: res.data.campaignStatus }))
        if (res.data.progressStatistics) {
          setProgressStatistics(res.data.progressStatistics)
        }
        if (res.data.campaignFirms?.totalRecordCount !== undefined) {
          setTotalElements(res.data.campaignFirms.totalRecordCount)
        }
        if (res.data.campaignFirms?.totalPageCount !== undefined) {
          setTotalPageCount(res.data.campaignFirms.totalPageCount)
        }
        if (res.data.campaignStatus) {
          setStoreCampaignStatus(queryParams.b2bCampaignId,
            res.data.campaignStatus)
        }
      }).finally(() => {
        setFetching(false)
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams.b2bCampaignId, setStoreCampaignStatus])

  useEffect(() => {
    const currentStatus = campaignStatus || campaignData?.status
    if (currentStatus === 'GENERATING' && !fetching) {
      intervalRef.current = setInterval(() => {
        handleRefresh()
      }, 7500)
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [campaignStatus, campaignData?.status, fetching, handleRefresh])

  const handlePageChange = useCallback((newPage) => {
    const filters = buildFilters()
    const sortingParams = buildSortingParams(sorting)

    setCurrentPage(newPage)

    handleRefresh(newPage, pageSize, filters, sortingParams)
  }, [handleRefresh, pageSize, buildFilters, sorting])

  const handlePageSizeChange = useCallback((newSize) => {
    const filters = buildFilters()
    const sortingParams = buildSortingParams(sorting)

    setPageSize(newSize)
    setCurrentPage(DEFAULT_PAGE)

    handleRefresh(DEFAULT_PAGE, newSize, filters, sortingParams)
  }, [handleRefresh, buildFilters, sorting])

  const handleSortingChange = useCallback((newSorting) => {
    const filters = buildFilters()
    const sortingParams = buildSortingParams(newSorting)

    setSorting(newSorting)
    setCurrentPage(DEFAULT_PAGE)

    handleRefresh(DEFAULT_PAGE, pageSize, filters, sortingParams)
  }, [handleRefresh, pageSize, buildFilters])

  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchTerm.length >= 3 || searchTerm.length === 0) {
        setDebouncedSearchTerm(searchTerm)
      }
    }, 500)

    return () => clearTimeout(handler)
  }, [searchTerm])

  useEffect(() => {
    if (queryParams?.b2bCampaignId && !fetching) {
      const filters = buildFilters()
      const sortingParams = buildSortingParams(sorting)

      setCurrentPage(DEFAULT_PAGE)

      handleRefresh(DEFAULT_PAGE, pageSize, filters, sortingParams)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suitabilityFilters, debouncedSearchTerm])

  const handleDownloadExcel = async () => {
    try {
      // In ready_to_mail status, we always export only selected firms
      const selected = true

      const suitabilityLevelOrders = suitabilityFilters.length > 0
        ? suitabilityFilters.map(opt => opt.value)
        : null

      const res = await downloadCampaignFirmsExcel(queryParams.b2bCampaignId, selected, suitabilityLevelOrders)
      const blob = new Blob([res.data], { type: 'application/octet-stream' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `campaign-firms-${queryParams.b2bCampaignId}.xlsx`
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      // error
    }
  }

  return (
    queryParamsError(queryParams, t('mailTemplate.wizard.b2bAccountIdError'),
      t('mailTemplate.wizard.b2bCampaignIdError')) ||
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <div className="p-2" style={{ direction: 'ltr' }}>
        <div className="flex justify-center items-center mb-4">
          <div className="flex flex-col items-center">
            <Email sx={{ fontSize: 50 }} color="primary"/>
            <Typography variant="caption" color="primary">
              {(campaignStatus || campaignData?.status) === 'GENERATING'
                ? t('mailTemplate.campaignReview.generating')
                : t('mailTemplate.campaignReview.readyToMail')
              }
            </Typography>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          {canStartMailing && (
            <>
              <Button
                variant="contained"
                onClick={handleDownloadExcel}
                sx={{ borderRadius: 2 }}
                endIcon={<TableViewIcon/>}
                disabled={!firms?.length}
              >
                {t('down_xls')}
              </Button>

              <Button
                variant="contained"
                onClick={handleStartMailingClick}
                disabled={startMailingLoading}
                startIcon={startMailingLoading
                  ? <CircularProgress
                    sx={{ color: 'inherit' }} size={18}/>
                  : null}
                endIcon={startMailingLoading
                  ? null
                  : <SendIcon/>}
                sx={{ borderRadius: 2 }}
              >
                {startMailingLoading
                  ? t(
                    'mailTemplate.campaignReview.startingMailing')
                  : t(
                    'mailTemplate.campaignReview.startMailing')}
              </Button>
            </>
          )}
        </div>
      </div>

      <Box sx={{
        p: 3,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
          {(campaignStatus || campaignData?.status) === 'GENERATING' && (
            <Tooltip
              title={isMailingInProgress
                ? t(
                  'mailTemplate.campaigns.detail.tooltips.campaignFirmsDisabled')
                : ''}
              placement="top"
            >
              <span>
                <IconButton
                  onClick={handleRefresh}
                  disabled={fetching || isMailingInProgress}
                  color="primary"
                  sx={{ borderRadius: 2 }}
                >
                  {fetching ? <CircularProgress size={24}/> : <Refresh/>}
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>

        <Box sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          <Autocomplete
            multiple
            size="small"
            limitTags={2}
            options={suitabilityOptions}
            disableCloseOnSelect
            openOnFocus
            getOptionLabel={(option) => option.label}
            value={suitabilityFilters}
            onChange={(_, newValue) => setSuitabilityFilters(newValue)}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <MuiCheckbox
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {option.label}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={suitabilityFilters.length === 0 ? t('mailTemplate.campaignPreparation.filter.suitabilityPlaceholder') : ''}
              />
            )}
            slotProps={{
              chip: {
                clickable: true
              }
            }}
            sx={{
              width: 280,
              '& .MuiAutocomplete-inputRoot': {
                flexWrap: 'nowrap',
                overflow: 'hidden',
                cursor: 'pointer'
              },
              '& .MuiAutocomplete-input': {
                minWidth: '30px !important'
              },
              '& .MuiAutocomplete-tag': {
                maxWidth: 100
              }
            }}
          />
            <TextField
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('mailTemplate.campaigns.search')}
              size="small"
              sx={{ width: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search/>
                  </InputAdornment>
                )
              }}
            />
            <FormControl sx={{ minWidth: 80 }}>
              <Select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(e.target.value)}
                size="small"
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </Select>
            </FormControl>
        </Box>
      </Box>

      {showProgressBar && (
        <CampaignProgressBar
          campaign={campaignData || campaign}
          progressStatistics={progressStatistics}
        />
      )}

      <Box sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        marginTop: '10px'
      }}>
        <CampaignReviewTable
          data={firms}
          onEdit={handleEditEmail}
          pageSize={pageSize}
          fetching={fetching}
          disableInteraction={isDisabled || isMailingInProgress}
          fuzzyFilter={fuzzyFilter}
          globalFilter={searchTerm}
          setGlobalFilter={setSearchTerm}
          campaignStatus={campaignStatus || campaignData?.status}
          currentPage={currentPage}
          totalElements={totalElements}
          totalPageCount={totalPageCount}
          onPageChange={handlePageChange}
          sorting={sorting}
          onSortingChange={handleSortingChange}
        />
      </Box>

      {!isMailingInProgress && (
        <>
          <EmailEditDialog
            open={emailEditState.open}
            handleClose={handleCloseEmailEdit}
            contactData={emailEditState.selectedContact}
            firmData={emailEditState.selectedFirm}
            onSave={handleSaveEmail}
            saving={emailEditState.saving}
          />
          <StartMailingConfirmDialog
            open={confirmMailingDialogOpen}
            onConfirm={handleConfirmStartMailing}
            onCancel={handleCancelStartMailing}
          />
        </>
      )}
    </Box>
  )
}

export default CampaignReview
